import { Link } from "react-router-dom";
import { Calendar, Flag, Smile, Moon, CheckCircle2, Printer, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const FEATURES = [
  { icon: Calendar, title: "See your week clearly", body: "A simple grid with colors and icons for every activity.", testid: "feature-week" },
  { icon: CheckCircle2, title: "Check off tasks", body: "Tick things off as you finish. It feels good.", testid: "feature-tasks" },
  { icon: Smile, title: "Track how you feel", body: "Pick a face each day for your mood and energy.", testid: "feature-mood" },
  { icon: Moon, title: "Quiet time blocks", body: "Plan calm breaks. Give yourself space to rest.", testid: "feature-quiet" },
  { icon: Flag, title: "Break big goals into small steps", body: "One tiny step at a time. You do not need to rush.", testid: "feature-goals" },
  { icon: Printer, title: "Print your plan", body: "See your week on paper. Put it on the fridge.", testid: "feature-print" },
];

export default function Landing() {
  return (
    <div className="min-h-screen" style={{ background: "var(--bg-primary)" }}>
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-9 h-9 rounded-xl bg-amber-400 flex items-center justify-center text-white font-bold">W</div>
            <span className="font-heading font-bold text-xl text-slate-800">My Week</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="header-login-link">
              <Button variant="ghost" className="rounded-full">Log in</Button>
            </Link>
            <Link to="/signup" data-testid="header-signup-link">
              <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-white">Sign up</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-24 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold uppercase tracking-widest mb-6" data-testid="hero-tag">
            <span className="w-2 h-2 rounded-full bg-amber-500"></span> A gentle weekly planner
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-slate-800 leading-tight">
            Plan your week. <br />
            <span className="text-amber-600">One kind step at a time.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-lg">
            My Week is a calm planner made for young autistic adults.
            See your days clearly. Add quiet time. Check off tasks. Feel good.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup" data-testid="hero-cta-signup">
              <Button size="lg" className="rounded-full bg-amber-500 hover:bg-amber-600 text-white px-8 h-12 text-base font-semibold">
                Start my week <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/login" data-testid="hero-cta-login">
              <Button size="lg" variant="outline" className="rounded-full h-12 px-8 text-base border-slate-300">
                I already have an account
              </Button>
            </Link>
          </div>
          <p className="mt-4 text-xs text-slate-500">Free to use. No ads. Your plan is private.</p>
        </div>

        <div className="relative">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
            <img
              src="https://images.unsplash.com/photo-1510519138101-570d1dca3d66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxjYWxtJTIwb3JnYW5pemVkJTIwZGVzayUyMHdhcm0lMjBsaWdodGluZ3xlbnwwfHx8fDE3ODYzMDQxOTl8MA&ixlib=rb-4.1.0&q=85"
              alt="A calm, organized desk"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="hidden sm:block absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm max-w-xs">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Moon className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-800">Quiet time</div>
                <div className="text-xs text-slate-500">3:00 - 3:30 pm</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
        <h2 className="font-heading font-bold text-3xl text-slate-800 mb-2">What you can do</h2>
        <p className="text-slate-600 mb-10">Simple tools. Clear words. No surprises.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map(({ icon: Icon, title, body, testid }) => (
            <div
              key={title}
              data-testid={testid}
              className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-amber-300 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-amber-700" />
              </div>
              <h3 className="font-heading font-bold text-lg text-slate-800 mb-1">{title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
        <div className="rounded-3xl p-10 sm:p-14 bg-amber-50 border border-amber-200 text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-800">Ready to start?</h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">Sign up in one minute. Add your first activity. See your week.</p>
          <Link to="/signup" data-testid="bottom-cta-signup">
            <Button size="lg" className="mt-6 rounded-full bg-amber-500 hover:bg-amber-600 text-white h-12 px-8 text-base font-semibold">
              Make my week
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 text-sm text-slate-500 flex flex-wrap justify-between gap-2">
          <span>© My Week. A friendly planner.</span>
          <span>Made with care.</span>
        </div>
      </footer>
    </div>
  );
}
